import { DEFAULT_BRAND_ID, resolveBrand } from "./index.js";

export const BRAND_PREVIEW_STORAGE_KEY = "caire.brand-preview.v1";

/** App-owned enabled flag; this package never reads an environment variable. */
export function createBrandPreview({
  enabled,
  document: providedDocument,
  getStorage,
  publishedBrand,
} = {}) {
  const doc =
    providedDocument === undefined ? globalThis.document : providedDocument;
  const storage = () =>
    getStorage ? getStorage() : globalThis.window?.localStorage;
  const listeners = new Set();
  const initial = resolveBrand(
    publishedBrand ?? doc?.documentElement.dataset.caireBrand,
  ).id;
  let published = initial;
  let publishedRevision = 0;
  let preview;
  if (enabled) {
    try {
      const stored = storage()?.getItem(BRAND_PREVIEW_STORAGE_KEY);
      if (stored != null) preview = resolveBrand(stored).id;
    } catch {
      /* Private browsing can reject even the storage getter. */
    }
  }
  let selected = preview ?? published;
  const apply = () => {
    if (!doc) return;
    doc.documentElement.dataset.caireBrand = selected;
    const meta = doc.querySelector('meta[name="theme-color"]');
    if (meta)
      meta.setAttribute("content", resolveBrand(selected).colors.action);
  };
  apply();
  const select = (id, reset = false) => {
    if (!enabled) return selected;
    const next = reset ? published : resolveBrand(id).id;
    preview = reset ? undefined : next;
    try {
      if (reset) storage()?.removeItem(BRAND_PREVIEW_STORAGE_KEY);
      else storage()?.setItem(BRAND_PREVIEW_STORAGE_KEY, next);
    } catch {
      /* Preview stays usable when persistence is unavailable. */
    }
    const changed = next !== selected;
    selected = next;
    apply();
    if (changed) listeners.forEach((notify) => notify());
    return selected;
  };
  return {
    enabled: Boolean(enabled),
    getSnapshot: () => selected,
    getPublishedRevision: () => publishedRevision,
    getServerSnapshot: () => initial,
    subscribe: (notify) => {
      listeners.add(notify);
      return () => {
        listeners.delete(notify);
      };
    },
    setBrand: (id) => select(id),
    reset: () => select(DEFAULT_BRAND_ID, true),
    setPublishedBrand: (id) => {
      publishedRevision += 1;
      published = resolveBrand(id).id;
      const next = preview ?? published;
      const changed = next !== selected;
      selected = next;
      apply();
      if (changed) listeners.forEach((notify) => notify());
      return selected;
    },
  };
}
